import React from 'react';

import MoreLink from 'components/MoreLink';
import { RoutePath } from 'enums/Routes';
import { useNavigate } from 'hooks/useNavigate';
import useOpenShiftImpactChat from 'hooks/useOpenShiftImpactChat';
import ShiftsList from 'components/FinancialCheckup/ShiftsList';
import { ReactComponent as ShiftwizardLogo } from 'images/shiftwizard-logo.svg';

import AISuggestionCard from './AISuggestionCard';
import { AvailableShift, useAvailableShifts } from './useAvailableShifts';

import styles from './AvailableShiftsSection.module.scss';

const MAX_LIST_SHIFTS = 3;
const NAVIGATE_TO_ALL_SHIFTS_EVENT = 'navigate_to_all_shifts';
const AI_SUGGESTION_CLICKED_EVENT = 'ai_suggestion_clicked';

const getClosestShift = (shifts: AvailableShift[]): AvailableShift | undefined => {
  if (shifts.length === 0) return undefined;

  const now = Date.now();

  return shifts.reduce((closest, shift) => {
    const closestDiff = Math.abs(new Date(closest.shiftStartDate).getTime() - now);
    const shiftDiff = Math.abs(new Date(shift.shiftStartDate).getTime() - now);

    return shiftDiff < closestDiff ? shift : closest;
  });
};

interface AvailableShiftsSectionProps {
  onClose?: () => void;
}

const AvailableShiftsSection: React.FC<AvailableShiftsSectionProps> = ({ onClose }) => {
  const { shifts, goalMessage, goalDescription } = useAvailableShifts();
  const navigate = useNavigate();
  const openShiftImpactChat = useOpenShiftImpactChat();

  if (!shifts || shifts.length === 0) return null;

  const featuredShift = getClosestShift(shifts);
  const remainingShifts = shifts.filter((shift) => shift.id !== featuredShift?.id);
  const visibleRemainingShifts = remainingShifts.slice(0, MAX_LIST_SHIFTS);
  const showMoreLink = remainingShifts.length > MAX_LIST_SHIFTS;

  const handleSeeAll = () => {
    analytics.track(NAVIGATE_TO_ALL_SHIFTS_EVENT);
    navigate(RoutePath.AvailableShifts);
  };

  const handleAISuggestionClick = () => {
    // Sanity check
    if (!featuredShift) return;

    onClose?.();
    analytics.track(AI_SUGGESTION_CLICKED_EVENT, { shift_id: featuredShift.id });
    openShiftImpactChat({ shiftId: featuredShift.shiftId });
  };

  return (
    <div className={styles.availableShiftsSection}>
      <div className={styles.header}>
        <div className={styles.poweredBy}>
          <span className={styles.poweredByText}>Powered by</span>
          <ShiftwizardLogo className={styles.shiftwizardLogo} />
        </div>
        <p className={styles.description}>See how every additional shift can accelerate your path towards your goals</p>
      </div>

      {featuredShift && (
        <div className={styles.nextShift}>
          <p className={styles.nextShiftLabel}>Next available shift</p>
          <ShiftsList shifts={[featuredShift]} />
        </div>
      )}

      <AISuggestionCard title={goalMessage} description={goalDescription} onClick={handleAISuggestionClick} />

      {(visibleRemainingShifts.length > 0 || showMoreLink) && (
        <div className={styles.labelContainer}>
          <p className={styles.sectionLabel}>More available shifts</p>
          {showMoreLink && <MoreLink onClick={handleSeeAll} />}
        </div>
      )}
      {visibleRemainingShifts.length > 0 && <ShiftsList shifts={visibleRemainingShifts} />}
    </div>
  );
};

export default AvailableShiftsSection;
